import type {ComponentProps} from 'react';
import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import cx from 'classnames';

import {
    Button,
    Color,
    FontVariant,
    Icon,
    IconTypes,
    Text,
    Tooltip,
} from '@pexip/components';
import type {AriaMenuItem} from '@pexip/components';

import {TestId} from '../../../test/testIds';
import type {InMeetingParticipant, ParticipantID, RoomID} from '../../types';
import {ParticipantRowActions} from '../ParticipantRowActions/ParticipantRowActions.view';
import type {RowActionState} from '../ParticipantRowActions/ParticipantRowActions.module';
import {BreakoutParticipantRowActions} from '../ParticipantRowActions/BreakoutParticipantRowActions.view';
import {ParticipantRowListItem} from '../ParticipantRowList/ParticipantRowList.view';

import styles from './ParticipantRow.module.scss';
import {highlightCharacters} from '../../utils/highlightCharacters';

const ParticipantAdmit: React.FC<{
    isTouchDevice: boolean;
    admit?: () => void;
}> = ({isTouchDevice, admit}) => {
    const {t} = useTranslation();
    const [isLoading, setLoading] = useState(false);

    const handleAdmit = () => {
        setLoading(true);
        admit?.();
    };

    return (
        <Tooltip text={t('meeting.admit', 'Admit')} position="top">
            <Button
                aria-label={t('meeting.admit', 'Admit')}
                noEnhancerPadding
                onClick={handleAdmit}
                variant="text"
                isLoading={isLoading}
                isDisabled={isLoading}
                size={isTouchDevice ? 'small' : 'compact'}
                data-testid={TestId.ButtonAdmitParticipant}
            >
                <Icon
                    source={IconTypes.IconAdmit}
                    color={Color.Join}
                    size={isTouchDevice ? 'small' : 'compact'}
                />
            </Button>
        </Tooltip>
    );
};

const ParticipantDeny: React.FC<{
    isTouchDevice: boolean;
    kick?: () => void;
}> = ({isTouchDevice, kick}) => {
    const {t} = useTranslation();
    const [isLoading, setLoading] = useState(false);

    const handleKick = () => {
        setLoading(true);
        kick?.();
    };

    return (
        <Tooltip text={t('meeting.deny', 'Deny')} position="top">
            <Button
                aria-label={t('meeting.deny', 'Deny')}
                noEnhancerPadding
                onClick={handleKick}
                variant="text"
                isLoading={isLoading}
                isDisabled={isLoading}
                size={isTouchDevice ? 'small' : 'compact'}
                data-testid={TestId.ButtonDenyParticipant}
            >
                <Icon
                    source={IconTypes.IconDeny}
                    color={Color.Red70}
                    size={isTouchDevice ? 'small' : 'compact'}
                />
            </Button>
        </Tooltip>
    );
};

export const ParticipantRowWrapper: React.FC<
    ComponentProps<'div'> & {
        isBreakout?: boolean;
        isTouchDevice?: boolean;
        showParticipantAuthenticatedState?: boolean;
        participant: InMeetingParticipant;
        isCurrentPeer?: boolean;
        onMouseEnter: () => void;
        onMouseLeave: () => void;
        highlightedCharacters?: {start: number; end: number}[];
    }
> = ({
    isBreakout = false,
    isTouchDevice,
    showParticipantAuthenticatedState = false,
    participant,
    isCurrentPeer,
    onMouseEnter,
    onMouseLeave,
    onPointerDownCapture,
    children,
    className,
    highlightedCharacters,
}) => {
    const {t} = useTranslation();
    return (
        // biome-ignore lint/a11y/noStaticElementInteractions: handlers only track hover/focus to reveal the row actions
        <div
            key={participant.uuid}
            className={cx(styles.participantRow, className)}
            onMouseEnter={onMouseEnter}
            onFocus={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onBlur={e => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                    onMouseLeave();
                }
            }}
            data-testid={TestId.ParticipantRow}
            onPointerDownCapture={onPointerDownCapture}
        >
            <div className={styles.participant}>
                {!isBreakout && showParticipantAuthenticatedState && (
                    <Tooltip
                        text={
                            participant.isIdpAuthenticated
                                ? t('meeting.authenticated', 'Authenticated')
                                : t(
                                      'meeting.unauthenticated',
                                      'Unauthenticated',
                                  )
                        }
                        position="topRight"
                    >
                        <Icon
                            className="mr-1"
                            source={
                                participant.isIdpAuthenticated
                                    ? IconTypes.IconAuthenticated
                                    : IconTypes.IconNotauthenticated
                            }
                            size={isTouchDevice ? 'medium' : 'small'}
                            color={
                                participant.isIdpAuthenticated
                                    ? Color.Neutral70
                                    : Color.SignalOrange130
                            }
                        />
                    </Tooltip>
                )}
                <Text
                    fontVariant={FontVariant.Body}
                    isTruncated
                    title={participant.displayName}
                    translate="no"
                >
                    {highlightedCharacters &&
                    highlightedCharacters.length > 0 &&
                    participant.displayName
                        ? highlightCharacters(
                              participant.displayName,
                              highlightedCharacters,
                              styles.mark,
                          )
                        : participant.displayName || 'User'}
                </Text>
                {isCurrentPeer && (
                    <Text
                        className={styles.metaText}
                        fontVariant={FontVariant.Body}
                        title={t('meeting.you', 'You')}
                        translate="no"
                    >
                        {'/ '}
                        {t('meeting.you', 'You')}
                    </Text>
                )}
                {participant.isHost && (
                    <Text
                        className={styles.metaText}
                        fontVariant={FontVariant.Body}
                        title={t('common.host', 'Host')}
                        translate="no"
                    >
                        {'/ '}
                        {t('common.host', 'Host')}
                    </Text>
                )}
            </div>
            <div className={styles.actionsWrapper}>
                <div className={styles.scrim} />
                {children}
            </div>
        </div>
    );
};

export const BreakoutParticipantRow: React.FC<
    ComponentProps<'div'> &
        Pick<
            ComponentProps<typeof ParticipantRowWrapper>,
            'participant' | 'isCurrentPeer'
        > & {
            onParticipantRoomChange?: (
                targetParticipantUuid: ParticipantID,
                targetRoomId: RoomID,
            ) => void;
            rooms?: Record<string, string>;
            isDragging: boolean;
        }
> = ({
    participant,
    isCurrentPeer,
    rooms,
    onParticipantRoomChange,
    isDragging,
    className,
}) => {
    const [isHover, setIsHover] = useState(false);

    const onMouseEnter = useCallback(() => setIsHover(true), []);
    const onMouseLeave = useCallback(() => setIsHover(false), []);

    const onRoomChange = useCallback(
        (roomId: RoomID) => {
            onParticipantRoomChange?.(participant.uuid, roomId);
        },
        [onParticipantRoomChange, participant.uuid],
    );

    return (
        <ParticipantRowWrapper
            isBreakout
            participant={participant}
            isCurrentPeer={isCurrentPeer}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className={className}
        >
            {rooms && Object.entries(rooms).length > 0 && (
                <BreakoutParticipantRowActions
                    rooms={rooms}
                    onRoomChange={onRoomChange}
                    isHover={isHover}
                    isDragging={isDragging}
                />
            )}
        </ParticipantRowWrapper>
    );
};

export const ParticipantRow: React.FC<
    Pick<RowActionState, 'isSpeaking'> & {
        isTouchDevice: boolean;
        isDirectMedia?: boolean;
        displayName: string;
        handleRemoveUserRequest: (
            participant: InMeetingParticipant,
            displayName: string,
        ) => void;
        handleTransfer?: (identity: ParticipantID) => void;
        handleDTMF?: (identity: ParticipantID) => void;
        handleFecc?: (identity: ParticipantID) => void;
        isCurrentPeer: boolean;
        canPerformHostActions?: boolean;
        isApiParticipant?: boolean;
        isHost: boolean;
        participant: InMeetingParticipant;
        additionalMenuContent?: AriaMenuItem[];
        displayLowerHandOnly?: boolean;
        handleLowerHand?: (identity: ParticipantID) => void;
        isSpotlightAvailable?: boolean;
        className?: string;
        highlightedCharacters?: {start: number; end: number}[];
    } & Pick<
            ComponentProps<typeof ParticipantRowWrapper>,
            'participant' | 'showParticipantAuthenticatedState'
        >
> = ({
    className,
    isTouchDevice,
    isDirectMedia,
    showParticipantAuthenticatedState,
    displayName,
    handleRemoveUserRequest,
    handleTransfer,
    handleDTMF,
    handleFecc,
    isCurrentPeer,
    canPerformHostActions = true,
    isApiParticipant,
    isHost,
    isSpeaking,
    participant,
    additionalMenuContent = [],
    displayLowerHandOnly,
    isSpotlightAvailable,
    handleLowerHand,
    highlightedCharacters,
}) => {
    const [isHover, setIsHover] = useState(false);

    const onMouseEnter = useCallback(() => setIsHover(true), []);
    const onMouseLeave = useCallback(() => setIsHover(false), []);

    return (
        <ParticipantRowListItem>
            <ParticipantRowWrapper
                className={className}
                participant={participant}
                showParticipantAuthenticatedState={
                    showParticipantAuthenticatedState
                }
                isCurrentPeer={isCurrentPeer}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                isTouchDevice={isTouchDevice}
                highlightedCharacters={highlightedCharacters}
            >
                {participant.isWaiting || participant.serviceType === 'ivr' ? (
                    <>
                        <ParticipantAdmit
                            isTouchDevice={isTouchDevice}
                            admit={participant?.admit}
                        />
                        <ParticipantDeny
                            isTouchDevice={isTouchDevice}
                            kick={participant?.kick}
                        />
                    </>
                ) : (
                    <ParticipantRowActions
                        displayLowerHandOnly={displayLowerHandOnly}
                        isTouchDevice={isTouchDevice}
                        showParticipantAuthenticatedState={
                            !!showParticipantAuthenticatedState
                        }
                        participant={participant}
                        isDirectMedia={isDirectMedia}
                        canPerformHostActions={canPerformHostActions}
                        isApiParticipant={isApiParticipant}
                        isHost={isHost}
                        isSpeaking={isSpeaking}
                        isHover={isHover}
                        isCurrentPeer={isCurrentPeer}
                        handleRemoveUserRequest={handleRemoveUserRequest}
                        handleTransfer={handleTransfer}
                        handleDTMF={handleDTMF}
                        handleFecc={handleFecc}
                        displayName={displayName}
                        onMouseLeave={onMouseLeave}
                        additionalMenuContent={additionalMenuContent}
                        handleLowerHand={handleLowerHand}
                        isSpotlightAvailable={isSpotlightAvailable}
                    />
                )}
            </ParticipantRowWrapper>
        </ParticipantRowListItem>
    );
};
export type ParticipantRowProps = React.ComponentProps<typeof ParticipantRow>;
